export type AppPlatform = 'web' | 'windows' | 'android';
export type AppUpdateMode = 'reload' | 'download';

export interface AppBuildInfo {
  version: string;
  buildNumber: string;
  commitSha: string;
  channel: string;
}

export interface AppReleasePlatformManifest {
  version: string;
  downloadUrl?: string;
  updateMode?: string;
  sha256?: string;
}

export interface AppReleaseManifest {
  channel: string;
  version: string;
  buildNumber: string;
  commitSha: string;
  releasedAtUtc?: string;
  minSupportedVersion: string;
  isAvailable: boolean;
  platforms: Partial<Record<AppPlatform, AppReleasePlatformManifest>>;
}

export interface AppUpdateState {
  platform: AppPlatform;
  currentVersion: string;
  latestVersion: string;
  minSupportedVersion: string;
  buildNumber: string;
  channel: string;
  hasUpdate: boolean;
  requiresUpdate: boolean;
  updateMode: AppUpdateMode;
  downloadUrl?: string;
  sha256?: string;
}

export function resolveNativeCall<T>(result: T | PromiseLike<T>): Promise<T> {
  return Promise.resolve(result);
}

export function isLocalDesktopDevelopmentRuntime(isDesktop: boolean, hostname: string): boolean {
  if (!isDesktop) {
    return false;
  }

  const normalizedHostname = hostname.trim().toLowerCase();
  return normalizedHostname === 'localhost'
    || normalizedHostname === '127.0.0.1'
    || normalizedHostname === '::1';
}

export function resolveAppUpdateState(
  current: AppBuildInfo,
  manifest: AppReleaseManifest | null,
  platform: AppPlatform,
  apiBaseUrl: string): AppUpdateState {
  const platformManifest = manifest?.platforms?.[platform];
  const latestVersion = normalizeVersion(platformManifest?.version || manifest?.version || current.version);
  const minSupportedVersion = normalizeVersion(manifest?.minSupportedVersion || current.version);
  const nativeManifestAvailable = platform !== 'web' && manifest?.isAvailable !== false;
  const nativeUpdateAvailable = nativeManifestAvailable && compareAppVersions(latestVersion, current.version) > 0;
  const requiresUpdate = nativeManifestAvailable && compareAppVersions(minSupportedVersion, current.version) > 0;
  const updateMode: AppUpdateMode = platform === 'web'
    ? 'reload'
    : 'download';
  const downloadUrl = updateMode === 'download'
    ? resolveDownloadUrl(platformManifest?.downloadUrl, apiBaseUrl)
    : undefined;

  return {
    platform,
    currentVersion: normalizeVersion(current.version),
    latestVersion,
    minSupportedVersion,
    buildNumber: current.buildNumber,
    channel: manifest?.channel || current.channel,
    hasUpdate: nativeUpdateAvailable,
    requiresUpdate,
    updateMode,
    downloadUrl,
    sha256: platformManifest?.sha256
  };
}

export function compareAppVersions(left: string, right: string): number {
  const a = parseAppVersion(left);
  const b = parseAppVersion(right);

  for (let index = 0; index < Math.max(a.parts.length, b.parts.length); index++) {
    const diff = (a.parts[index] ?? 0) - (b.parts[index] ?? 0);
    if (diff !== 0) {
      return diff;
    }
  }

  if (a.prerelease && !b.prerelease) return -1;
  if (!a.prerelease && b.prerelease) return 1;
  if (!a.prerelease && !b.prerelease) return 0;
  return a.prerelease.localeCompare(b.prerelease);
}

function resolveDownloadUrl(downloadUrl: string | undefined, apiBaseUrl: string): string | undefined {
  if (!downloadUrl) {
    return undefined;
  }

  if (/^https?:\/\//i.test(downloadUrl)) {
    return downloadUrl;
  }

  const apiOrigin = trimApiSuffix(apiBaseUrl);
  return `${apiOrigin}/${downloadUrl.replace(/^\/+/, '')}`;
}

function trimApiSuffix(apiBaseUrl: string): string {
  return trimTrailingSlash(apiBaseUrl).replace(/\/api$/i, '');
}

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '');
}

function normalizeVersion(value: string): string {
  return value.trim().replace(/^v/i, '') || '0.0.0';
}

function parseAppVersion(value: string): { parts: number[]; prerelease: string } {
  const [core, prerelease = ''] = normalizeVersion(value).split('-', 2);
  const parts = core.split('.').map((part) => {
    const parsed = Number.parseInt(part, 10);
    return Number.isFinite(parsed) ? parsed : 0;
  });

  return { parts, prerelease };
}
